import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiFolder, FiX, FiDownload, FiTrash2, FiFile, FiEye } from 'react-icons/fi';
import { InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const LibrarySection = ({ department }) => {
  // Document types for each department
  const documentTypes = {
    quality: [
      'Thermography reports',
      'Warranty Certificates',
      'Site EL Inspection reports',
      'Site Flash testing reports',
      'Work Instruction',
      'Checklist/ Forms',
      'Any Other Reports',
    ],
    engineering: [
      'Datasheets',
      'Module Layup Drawing',
      'Module Mechanical drawings',
      'BIS test reports',
      'ALMM approval',
      'IEC 61215-1 &-2 test reports',
      'IEC 61730-1 & -2 test reports',
      'User Manual',
      'Warranty Manual',
      'Cleaning Manual',
      'Warranty Claim documents',
      'Any Other Reports',
    ],
  };

  // State
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Fetch projectId from localStorage
  const projectId = localStorage.getItem('selectedProject');

  // Fetch documents when component mounts or projectId/department changes
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!projectId || projectId.trim() === '') {
        setLoading(false);
        setError('projectId is required to fetch documents. Please select a project.');
        return;
      }
      if (!department || !documentTypes[department.toLowerCase()]) {
        setLoading(false);
        setError('Invalid or missing department.');
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${baseURL}get_documents`, {
          params: { projectId, department },
        });
        const backendDocs = response.data.documents || [];

        // Filter backend documents to include only the department's document types
        const filteredDocs = documentTypes[department.toLowerCase()].map((title, index) => {
          const matchedDoc = backendDocs.find(doc => doc.title === title);
          return {
            id: `${index + 1}`,
            title,
            files: matchedDoc?.files || [],
          };
        });

        setDocuments(filteredDocs);
        setFilteredDocuments(filteredDocs);
      } catch (err) {
        const errorMessage = err.response?.data?.error || err.message;
        setError(`Failed to fetch documents: ${errorMessage}`);
        console.error(`Error for ${department}:`, err.response?.data || err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, [projectId, department]);

  // Open modal and set selected document
  const handleCardClick = (document) => {
    setSelectedDocument(document);
    setOpenModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedDocument(null);
  };

  // Handle file deletion with backend integration
  const handleDeleteFile = async (fileId) => {
    const remark = prompt('Please provide a remark for deleting this file:');
    if (!remark || remark.trim() === '') {
      alert('Remark is required to delete the file.');
      return;
    }
    const documentId = fileId.split('_')[0];
    try {
      const response = await axios.delete(`${baseURL}delete_document`, {
        params: {
          documentId,
          fileId,
          remark: remark.trim(),
        },
      });
      setDocuments((prevDocuments) =>
        prevDocuments.map((doc) =>
          doc.id === selectedDocument.id
            ? {
                ...doc,
                files: doc.files.filter((file) => file.id !== fileId),
              }
            : doc
        )
      );
      alert(response.data.message || 'File deleted successfully.');
      window.location.reload();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to delete file. Please try again.';
      alert(errorMessage);
      console.error('Error deleting file:', err);
    }
  };

  // Handle search input change
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = documents.map((doc) => {
      const titleMatch = doc.title.toLowerCase().includes(query);

      const matchedFiles = doc.files.filter((file) =>
        file.name.toLowerCase().includes(query)
      );

      return {
        ...doc,
        files: matchedFiles,
        isVisible: titleMatch || matchedFiles.length > 0,
      };
    });

    setFilteredDocuments(filtered.filter(doc => doc.isVisible));
  };

  return (
    <div className="p-6">
      {/* Main Heading */}
      <h1 className="text-2xl font-bold text-center mb-6">LIBRARY</h1>

      {/* Error Message */}
      {error && <p className="text-center text-red-600 mb-4">{error}</p>}

      {/* Search Bar */}
      <div className="mb-6">
        <TextField
          type="text"
          placeholder="Search documents..."
          value={searchQuery}
          onChange={handleSearch}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#fff',
              border: '1px solid #d1d5db',
              '&:hover': {
                borderColor: '#9ca3af',
              },
            },
          }}
        />
      </div>

      {/* Loading Indicator */}
      {loading && (
        <div className="flex justify-center mb-6">
          <div className="w-8 h-8 border-4 border-t-transparent border-gray-600 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Document Cards */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {filteredDocuments.length > 0 ? (
            filteredDocuments.map((document) => (
              <div
                key={document.id}
                className="p-4 h-40 flex flex-col justify-between bg-white rounded-lg shadow-md cursor-pointer transform transition-transform hover:scale-105"
                onClick={() => handleCardClick(document)}
              >
                <div>
                  <h2 className="text-lg">{document.title}</h2>
                  <p className="text-sm text-gray-500">{document.files.length} file(s)</p>
                </div>
                <div className="text-right">
                  <button className="px-3 py-1 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-md">
                    View Files
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center col-span-full">No documents found.</p>
          )}
        </div>
      )}

      {/* Modal for File Preview */}
      {openModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300]">
          <div className="bg-white rounded-lg shadow-lg p-6 w-11/12 max-h-[90vh] overflow-y-auto relative">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl">{selectedDocument?.title}</h2>
              <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                <FiX size={24} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {selectedDocument?.files.length > 0 ? (
                selectedDocument.files.map((file) => (
                  <div
                    key={file.id}
                    className="p-4 flex flex-col justify-between bg-white rounded-lg shadow-md"
                  >
                    {/* Thumbnail or Icon */}
                    {file.url && (file.url.includes('.jpg') || file.url.includes('.png')) ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        className="w-full h-24 object-cover rounded-md"
                      />
                    ) : (
                      <FiFile className="text-5xl mx-auto my-4 text-gray-500" />
                    )}
                    <div>
                      <p className="text-sm truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        Uploaded: {new Date(file.uploadDate).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        Remark: {file.remark || 'N/A'}
                      </p>
                      {file.customDocumentType && (
                        <p className="text-xs text-gray-500">
                          Custom Type: {file.customDocumentType}
                        </p>
                      )}
                    </div>
                    {/* Actions */}
                    <div className="flex justify-between mt-2">
                      <button
                        onClick={() => window.open(file.url, '_blank')}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        <FiEye size={20} />
                      </button>
                      <button
                        onClick={() => handleDeleteFile(file.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FiTrash2 size={20} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center col-span-full">No matching files found.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibrarySection;